import { memo } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import styled from 'styled-components'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/opacity.css'
import { isSafari } from 'utils/helpers'

const PREPEND = {
  svg: 'data:image/svg;base64,',
  png: 'data:image/png;base64,',
}

const Image = styled(LazyLoadImage)
.withConfig({
    shouldForwardProp: prop => ![
      'bg',
      'fit',
      'circle',
      // 'growX',
    ].includes(prop),
  })`
  ${props => ({
    WebkitTransform: 'translate3d(0, 0, 0)',
    width: props.$growX ? '100%' : 'auto',
    height: props.$growY ? '100%' : 'auto',
    maxWidth: props.$styledWidth || '100%',
    maxHeight: props.$styledHeight || '100%',
    ...(props.$fluid && { width: '100%' }),
    ...(props.fit && { objectFit: props.fit, height: '100%', width: '100%' }),
    ...(props.$objectPosition && { objectPosition: props.$objectPosition }),
    ...(props.circle && { borderRadius: '50%', overflow: 'hidden', height: props.circle, width: props.circle, maxHeight: props.circle, maxWidth: props.circle }),
  })}
`

const Img = props => {
  const {
    bg,
    width,
    height,
    ratioWidth,
    ratioHeight,
    fluid,
    growY,
    growX,
    objectPosition,
    noEffect,
    src,
    alt,
    wrapperProps,
    ...otherProps
  } = props

  if (bg) {
    return (
      <Box 
        sx={{
          ...(bg ? {
            background: `url(${PREPEND[otherProps.prepend] || ''}${src})`,
          } : {}),
          height: height || '100%',
          width: width || '100%',
          backgroundRepeat: otherProps.repeat ? 'repeat' : 'no-repeat',
          backgroundPosition: `${otherProps.x || 'center'} ${otherProps.y || 'center'}`,
          backgroundSize: otherProps.size || 'cover',
          backgroundAttachment: otherProps.parallax ? 'fixed' : undefined,
          backgroundColor: typeof bg === 'string' ? bg : undefined,
          ...(otherProps.circle ? {
            borderRadius: '50%',
            overflow: 'hidden',
            height: otherProps.circle,
            width: otherProps.circle,
          } : {}),
          ...(otherProps.ratio ? {
            width: '100%',
            height: 'auto;',
            paddingTop: otherProps.ratio === '16/9' ? '56.25%' : otherProps.ratio === '3/4' ? '75%' : otherProps.ratio === '2/1' ? '50%' : otherProps.ratio === '1/1' ? '100%' : '',
          } : {}),
        }}
        {...otherProps}
      />
    )
  }

  return (
    <Image
      $styledWidth={width}
      $styledHeight={height}
      width={ratioWidth}
      height={ratioHeight}
      $fluid={fluid}
      $growY={growY}
      $growX={growX}
      $objectPosition={objectPosition}
      effect={isSafari || noEffect ? undefined : 'opacity'}
      wrapperProps={
        noEffect
          ? undefined
          : {
              style: {
                display: 'inline-block',
                width: '100%',
                height: '100%',
                ...wrapperProps
              },
            }
      }
      {...otherProps}
      src={`${PREPEND[otherProps.prepend] || ''}${src}`}
      alt={alt || undefined}
    />
  )
}

Img.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  srcSet: PropTypes.object,
  alt: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  className: PropTypes.string,
  bg: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  parallax: PropTypes.bool,
  repeat: PropTypes.bool,
  growY: PropTypes.bool,
  growX: PropTypes.bool,
  x: PropTypes.number,
  y: PropTypes.number,
  size: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  ratioHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ratioWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  circle: PropTypes.string,
  fluid: PropTypes.bool,
  noEffect: PropTypes.bool,
  objectPosition: PropTypes.string,
  wrapperProps: PropTypes.object,
  ratio: PropTypes.oneOf(['16/9', '3/4', '2/1', '1/1']),
  fit: PropTypes.oneOf(['contain', 'cover']),
  prepend: PropTypes.oneOf(['png', 'svg']),
}

Img.defaultProps = {
  wrapperProps: {},
}

export default memo(Img)